<template>
  <div class="social-buttons">
    <p v-if="yandexEmailRequired" class="yandex-email-hint" role="status">
      {{ t('auth.yandexEmailRequired') }}
    </p>
    <a
      v-if="mode !== 'register'"
      :href="googleUrl"
      class="social-btn social-btn--wide google"
      @click="rememberTradeInReturn"
    >
      <img :src="GoogleIcon" class="social-icon" alt="Google" />
      <span>{{ googleLabel }}</span>
    </a>
    <button
      v-else
      class="social-btn social-btn--wide google"
      @click="handleGoogleClick"
    >
      <img :src="GoogleIcon" class="social-icon" alt="Google" />
      <span>{{ googleLabel }}</span>
    </button>

    <a :href="yandexUrl" class="social-btn social-btn--wide yandex" @click="rememberTradeInReturn">
      <img :src="YandexIcon" class="social-icon" alt="" />
      <span>{{ t('auth.loginWithYandex') }}</span>
    </a>

    <!-- Вход через Telegram — общий компонент (челлендж, окно ожидания, опрос),
         его же берёт мост входа по ключу. -->
    <TelegramLoginButton
      :mode="mode"
      :inviter-login="inviterLogin"
      :stay-on-site="stayOnSite"
      :tradein-resume="tradeinResume"
      @totp-required="emit('totp-required', $event)"
    />

    <SponsorPopup v-model="showSponsorPopup" @confirmed="onSponsorConfirmed" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import GoogleIcon from '@/assets/images/icons/google.svg'
import YandexIcon from '@/assets/images/icons/yandex.svg'
import SponsorPopup from './SponsorPopup.vue'
import TelegramLoginButton from './TelegramLoginButton.vue'
import { consumeTradeInAuthReturn, isTradeInResumeId, storeTradeInAuthReturn } from '@/modules/vpn/utils/tradeinClaim'

const props = defineProps<{
  /** Mode: 'login' or 'register' */
  mode?: 'login' | 'register'
  /** Optional inviter login for telegram referral link */
  inviterLogin?: string
  /** FLOW-V5 §3.2 — вход со страницы переноса: см. TelegramLoginButton. */
  stayOnSite?: boolean
  tradeinResume?: string | null
}>()

const emit = defineEmits<{
  /** OTP-R6c: вход через Telegram ждёт код из приложения — шаг показывает окно входа. */
  (e: 'totp-required', pendingToken: string): void
}>()

const { t } = useI18n()
const yandexEmailRequired = new URLSearchParams(window.location.search).get('yandex_email_required') === '1'

// ── Sponsor Popup State (Google; у Telegram своё окно внутри TelegramLoginButton) ──

const showSponsorPopup = ref(false)

// ── Base58 (for Google state) ───────────────────

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz'

const encodeBase58 = (str: string): string => {
  const bytes = new TextEncoder().encode(str)
  let num = 0n
  for (const byte of bytes) {
    num = num * 256n + BigInt(byte)
  }
  if (num === 0n) return BASE58_ALPHABET[0]
  let result = ''
  while (num > 0n) {
    const remainder = Number(num % 58n)
    result = BASE58_ALPHABET[remainder] + result
    num = num / 58n
  }
  for (const byte of bytes) {
    if (byte === 0) {
      result = BASE58_ALPHABET[0] + result
    } else {
      break
    }
  }
  return result
}

// ── Helpers ─────────────────────────────────────

const getReferralCode = () => {
  const stored = props.inviterLogin || localStorage.getItem('partner_referral_code')
  if (stored) return stored
  const match = document.cookie.match(/(^| )referal=([^;]+)/)
  return match ? decodeURIComponent(match[2]) : ''
}

// ── Computed ────────────────────────────────────

const buildGoogleUrl = (refCode?: string) => {
  const stateObj: Record<string, string> = {}
  if (refCode) stateObj.referal = refCode
  const state = Object.keys(stateObj).length > 0
    ? encodeBase58(JSON.stringify(stateObj))
    : 'AQ4'
  return props.mode === 'register'
    ? `/app/profile/google-signup?state=${state}`
    : `/app/profile/google-signin?state=${state}`
}

const yandexUrl = computed(() => {
  const referral = getReferralCode()
  return referral ? `/app/auth/yandex?${new URLSearchParams({ referal: referral })}` : '/app/auth/yandex'
})

const googleUrl = computed(() => buildGoogleUrl(getReferralCode()))

const googleLabel = computed(() =>
  props.mode === 'register' ? t('auth.registerWithGoogle') : t('auth.loginWithGoogle'),
)

// ── Sponsor Popup Logic ─────────────────────────

const needsSponsorPopup = () => {
  return props.mode === 'register' && !getReferralCode()
}

const rememberTradeInReturn = () => {
  const pendingReturn = yandexEmailRequired && window.location.pathname === '/registration'
    ? consumeTradeInAuthReturn()
    : null
  const path = isTradeInResumeId(props.tradeinResume)
    ? `/tradein/s/${props.tradeinResume}`
    : pendingReturn ?? window.location.pathname
  storeTradeInAuthReturn(path)
}

const onSponsorConfirmed = (login: string | null) => {
  rememberTradeInReturn()
  window.location.href = buildGoogleUrl(login ?? undefined)
}

// ── Google Click Handler ────────────────────────

const handleGoogleClick = () => {
  if (needsSponsorPopup()) {
    showSponsorPopup.value = true
  } else {
    rememberTradeInReturn()
    window.location.href = googleUrl.value
  }
}

</script>

<style scoped lang="scss">
.social-buttons {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.social-btn {
  width: 60px;
  height: 48px;
  border-radius: var(--ds-radius-control);
  border: 1px solid var(--ds-border);
  background: var(--ds-surface);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--ds-text-size-h-md);
  cursor: pointer;
  transition: background 0.2s;
  text-decoration: none;
  color: inherit;

  &:hover {
    background: var(--ds-surface-raised);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.social-btn--wide {
  width: 100%;
  gap: 10px;
  padding: 0 16px;
}

.social-btn--wide span {
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-semibold);
}

.yandex-email-hint {
  margin: 0;
  color: var(--ds-text-secondary);
  font-size: var(--ds-text-size-body);
}

.social-btn.yandex {
  background: var(--ds-yandex-bg);
  color: var(--ds-yandex-text);
  border-color: transparent;

  &:hover {
    background: var(--ds-yandex-bg-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--ds-text);
    outline-offset: 3px;
  }
}

.social-btn.google {
  background: var(--ds-surface);
  color: var(--ds-text-secondary);
  border: 1px solid #ddd;

  &:hover:not(:disabled) {
    background: var(--ds-surface-raised);
    border-color: #ccc;
  }
}

.social-icon {
  width: 20px;
  height: 20px;
}
</style>
